import { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import axiosInstance from '@api/axios.ts';
import { urls } from '@api/urls';
import useToast from '@hooks/useToast';
import {
  EMPTY_FIRM_PROFILE,
  FirmProfile,
  FirmProfileResponse,
  firmProfileErrorMessage,
  letterheadPreviewLines,
  LOGO_MIME_TYPES,
  logoFileProblem,
  signaturePreviewLines,
} from '@features/settings-firm-profile/utils/signature';

type FieldConfig = {
  key: keyof FirmProfile;
  label: string;
  multiline?: boolean;
  helperText?: string;
};

const FIELDS: FieldConfig[] = [
  { key: 'firm_name', label: 'Firm name' },
  {
    key: 'address',
    label: 'Firm address',
    multiline: true,
    helperText: 'One line per row, as it should print on the letterhead.',
  },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'attorney_name', label: 'Attorney name' },
  { key: 'attorney_title', label: 'Attorney title' },
  { key: 'bar_number', label: 'Bar number' },
  {
    key: 'signature_block',
    label: 'Custom signature block (optional)',
    multiline: true,
    helperText:
      'When filled in, printed as written instead of the name, title and bar number lines.',
  },
];

/** The logo as the letter prints it: at most 3 in wide and 1 in tall. */
const LOGO_PREVIEW_SX = {
  display: 'block',
  maxWidth: '3in',
  maxHeight: '1in',
  objectFit: 'contain',
} as const;

/**
 * Firm profile (ZLAYT-1410): captured once, printed as the letterhead and
 * signature block of every demand letter instead of placeholders. The
 * letterhead logo (ZLAYT-1428) is uploaded on its own endpoint, so it can be
 * changed without saving the text fields.
 */
function SettingsFirmProfileForm() {
  const { showSuccess, showError } = useToast();
  const [profile, setProfile] = useState<FirmProfile>(EMPTY_FIRM_PROFILE);
  const [canEdit, setCanEdit] = useState(false);
  const [scope, setScope] = useState<'company' | 'user'>('user');
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);

  const apply = (data: FirmProfileResponse) => {
    const {
      scope: nextScope,
      can_edit: nextCanEdit,
      logo_url: nextLogoUrl,
      ...fields
    } = data;
    setProfile({ ...EMPTY_FIRM_PROFILE, ...fields });
    setScope(nextScope);
    setCanEdit(nextCanEdit);
    setLogoUrl(nextLogoUrl ?? null);
  };

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadFailed(false);
    axiosInstance({ method: 'GET', url: urls.firmProfile.base })
      .then((res) => {
        if (cancelled) return;
        apply(res.data as FirmProfileResponse);
        setLoaded(true);
      })
      .catch(() => {
        // Rendered inline (with Retry) rather than as the company notice:
        // canEdit is still at its initial false here, which says nothing
        // about who owns the profile.
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const onSave = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance({
        method: 'PUT',
        url: urls.firmProfile.base,
        data: profile,
      });
      apply(res.data as FirmProfileResponse);
      showSuccess(
        'Changes saved',
        'New demand letters will use this letterhead and signature.'
      );
    } catch (error) {
      showError(
        'Error',
        firmProfileErrorMessage(error, 'The firm profile could not be saved.')
      );
    } finally {
      setSaving(false);
    }
  };

  const onLogoChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again still fires a change.
    event.target.value = '';
    if (!file) return;
    const problem = logoFileProblem(file);
    if (problem) {
      showError('Error', problem);
      return;
    }
    const data = new FormData();
    data.append('logo', file);
    setLogoBusy(true);
    try {
      const res = await axiosInstance({
        method: 'POST',
        url: urls.firmProfile.logo,
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl((res.data as FirmProfileResponse).logo_url ?? null);
      showSuccess(
        'Logo saved',
        'New demand letters will print it at the top of the letterhead.'
      );
    } catch (error) {
      showError(
        'Error',
        firmProfileErrorMessage(error, 'The logo could not be uploaded.')
      );
    } finally {
      setLogoBusy(false);
    }
  };

  const onRemoveLogo = async () => {
    setLogoBusy(true);
    try {
      const res = await axiosInstance({
        method: 'DELETE',
        url: urls.firmProfile.logo,
      });
      setLogoUrl((res.data as FirmProfileResponse).logo_url ?? null);
      showSuccess(
        'Logo removed',
        'Letters already produced keep the logo they were made with.'
      );
    } catch (error) {
      showError(
        'Error',
        firmProfileErrorMessage(error, 'The logo could not be removed.')
      );
    } finally {
      setLogoBusy(false);
    }
  };

  const letterheadLines = letterheadPreviewLines(profile);
  const firmName = (profile.firm_name || '').trim();
  const hasFirmName = Boolean(firmName);
  const logoAlt = firmName ? `${firmName} firm logo` : 'Firm logo';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Firm profile
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Printed as the letterhead and signature block of your demand letters.
        {scope === 'company' && ' Shared by everyone in your company.'}
      </Typography>
      {loadFailed && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setLoadAttempt((n) => n + 1)}
            >
              Retry
            </Button>
          }
        >
          The firm profile could not be loaded.
        </Alert>
      )}
      {loaded && scope === 'company' && !canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This profile belongs to your company. Only a company admin can change
          it.
        </Alert>
      )}
      <Divider sx={{ mb: 3 }} />
      <Stack spacing={2.5} maxWidth={640}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Letterhead logo
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Optional. PNG or JPEG, up to 2 MB. Printed at the top of new demand
            letters, at most 3 inches wide and 1 inch tall.
          </Typography>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt={logoAlt}
              sx={{ ...LOGO_PREVIEW_SX, mb: 1 }}
            />
          )}
          {canEdit && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                component="label"
                disabled={!loaded || logoBusy}
              >
                {logoUrl ? 'Replace logo' : 'Upload logo'}
                <input
                  hidden
                  type="file"
                  aria-label="Upload logo"
                  accept={LOGO_MIME_TYPES.join(',')}
                  onChange={onLogoChosen}
                  disabled={!loaded || logoBusy}
                />
              </Button>
              {logoUrl && (
                <Button
                  color="error"
                  onClick={onRemoveLogo}
                  disabled={!loaded || logoBusy}
                >
                  Remove logo
                </Button>
              )}
            </Stack>
          )}
        </Box>

        {FIELDS.map((field) => (
          <TextField
            key={field.key}
            id={`firm-profile-${field.key}`}
            label={field.label}
            value={profile[field.key]}
            onChange={(event) =>
              setProfile((prev) => ({
                ...prev,
                [field.key]: event.target.value,
              }))
            }
            multiline={field.multiline}
            minRows={field.multiline ? 3 : undefined}
            helperText={field.helperText}
            disabled={!loaded || !canEdit}
            fullWidth
          />
        ))}

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Letter preview
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              fontFamily: "'Times New Roman', Times, serif",
            }}
          >
            {(logoUrl || letterheadLines) && (
              <Box textAlign="center" mb={2}>
                {logoUrl && (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt={logoAlt}
                    sx={{ ...LOGO_PREVIEW_SX, mx: 'auto', mb: 0.75 }}
                  />
                )}
                {letterheadLines?.map((line, index) => (
                  <Typography
                    key={`${index}-${line}`}
                    sx={{
                      fontFamily: 'inherit',
                      fontWeight: index === 0 && hasFirmName ? 700 : 400,
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
            <Typography sx={{ fontFamily: 'inherit' }}>Sincerely,</Typography>
            <br />
            {signaturePreviewLines(profile).map((line, index) => (
              <Typography
                key={`${index}-${line}`}
                sx={{ fontFamily: 'inherit' }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>

        {canEdit && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={onSave}
              disabled={!loaded || saving}
            >
              Save
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default SettingsFirmProfileForm;
